import { useCallback, useState } from 'react';

import type { GetContextMenuItemsParams, MenuItemDef } from 'ag-grid-community';
import { confirmPopup, ConfirmPopupProps } from 'primereact/confirmpopup';
import { ProgressBar } from 'primereact/progressbar';
import { Toast } from 'primereact/toast';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import type { AuthenticationType } from '@/app/slices/authentication';
import { useConfirm } from '@/components/ui/confirm-dialog';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useIsTogActive } from '@/hooks/useIsTogActive';
import {
  type PastDueRestrictionResult,
  useShipmentPastDueRestriction,
} from '@/hooks/useShipmentPastDueRestriction';
import { useGenerateShippingForm } from '@/pages/shipping-details/hooks/useGenerateShippingForm';
import { useShouldPromptSemenLab } from '@/pages/shipping-details/hooks/useSemenLabPrompt';
import { useAccessToken } from '@/services/auth';
import { setInProgress } from '@/services/shipping';
import { useShipToIntraWorkflow, useShipWorkflow } from '@/services/workflows';
import { auditHistoryMenuItem } from '@/utils/context-menu';
import { runWorkflow, WORKFLOW } from '@/utils/workflow';

import { ShipmentStatus } from '../../utils/enums';

type Row = {
  id: string;
  hasSemen?: boolean;
  shipFromSiteId?: string | null;
  shipmentStatus?: number | null;
  shipmentNumber?: string | null;
  owner?: {
    id?: string | null;
    code?: string | null;
    name?: string | null;
  } | null;
};

type ToastRef = React.RefObject<Toast>;

export function useShippingContextMenu(
  toastRef?: ToastRef,
  onEditSelected?: (ids: string[]) => void,
) {
  const token = useAccessToken();
  const { t } = useTranslation();
  const confirmWorkflow = useConfirm();

  const generateShippingForm = useGenerateShippingForm();
  const enableShippingFormExport = useFeatureFlag('shipping_form_export');
  const [pendingExport, setPendingExport] = useState<{
    shipmentIds: string[];
    format: 'pdf' | 'excel';
  } | null>(null);
  const shouldPromptSemenLab = useShouldPromptSemenLab();
  const showPedigreeNames = useFeatureFlag(
    'shipping_show_report_params_dialog',
  );
  const confirmWorkflows = useFeatureFlag('shipping_confirm_workflows');
  const {
    restrictionEnabled: pastDueRestrictionEnabled,
    checkOwners: checkPastDueOwners,
  } = useShipmentPastDueRestriction();

  const environmentId =
    useSelector(
      (state: { authentication: AuthenticationType }) =>
        state.authentication.me?.environmentId,
    ) || 0;

  const confirm = useCallback(
    (options: ConfirmPopupProps) =>
      new Promise<boolean>((resolve) => {
        confirmPopup({
          ...options,
          accept: () => resolve(true),
          reject: () => resolve(false),
        });
      }),
    [],
  );

  const withToast = useCallback(
    <Args extends unknown[], Result>(
      label: string,
      fn: (...args: Args) => Promise<Result>,
    ) => {
      return async (...args: Args): Promise<Result> => {
        toastRef?.current?.show({
          className: 'bg-white border border-gray-200 shadow-sm',
          sticky: true,
          closable: false,
          content: (
            <div className="flex w-full flex-col items-start gap-2">
              <div className="flex items-center gap-2">
                <i className="pi pi-cog pi-spin text-2xl" />
                <span className="font-medium">{label}</span>
              </div>
              <ProgressBar mode="indeterminate" className="h-1 w-full" />
            </div>
          ),
        });

        try {
          const result = await fn(...args);

          toastRef?.current?.clear();
          toastRef?.current?.show({
            severity: 'success',
            summary: t('common.complete'),
            detail: label,
            life: 2500,
          });

          return result;
        } catch (err) {
          const networkError =
            typeof err === 'object' && err !== null && 'networkError' in err
              ? err.networkError
              : undefined;
          const message =
            (err instanceof Error ? err.message : undefined) ||
            (networkError instanceof Error
              ? networkError.message
              : undefined) ||
            t('common.somethingWentWrongNotifyHelpDesk');
          toastRef?.current?.clear();
          toastRef?.current?.show({
            severity: 'error',
            summary: t('common.error'),
            detail: `${label}: ${message}`,
            life: 5000,
          });
          throw err;
        }
      };
    },
    [t, toastRef],
  );

  const showPastDueRestriction = useCallback(
    (
      { accountNames, verificationFailed }: PastDueRestrictionResult,
      messageKey: string,
    ) => {
      toastRef?.current?.clear();
      toastRef?.current?.show({
        severity: 'error',
        summary: t('shipping.pastDueRestriction'),
        detail: verificationFailed
          ? t('shipping.pastDueRestrictionCheckFailed')
          : t(messageKey, { accounts: accountNames.join(', ') }),
        life: 5000,
      });
    },
    [t, toastRef],
  );

  const printForms = useCallback(
    async (rows: Row[], api: GetContextMenuItemsParams['api']) => {
      const shouldEmail = shouldPromptSemenLab(rows)
        ? await confirm({
            message: t('semen.emailSemenLab'),
            icon: 'pi pi-question-circle',
            defaultFocus: 'accept',
          })
        : false;

      await withToast(
        `Printing Shipping Form(s) (${rows.length})`,
        async () => {
          const ids = rows.map((r) => r.id);
          const shipmentNumbers = rows
            .map((r) => r.shipmentNumber)
            .filter((c): c is string => !!c);
          await generateShippingForm({
            sendEmail: shouldEmail,
            shipmentIds: ids,
            shipmentNumbers,
            showPedigree: showPedigreeNames,
          });

          api.deselectAll?.();
          api.clearRangeSelection?.();
          api.clearFocusedCell?.();
        },
      )();
    },
    [
      confirm,
      generateShippingForm,
      shouldPromptSemenLab,
      showPedigreeNames,
      t,
      withToast,
    ],
  );

  const nativeShippingEnabled = useFeatureFlag('shipping_native_workflows');
  const isUserValidationEnabled = useFeatureFlag('Workflow_User_Validation');
  const isTogActive = useIsTogActive();
  // INTRA storage locations exist only in the TOG database.
  const canShipToIntra = !isUserValidationEnabled || isTogActive;
  const [ship] = useShipWorkflow();
  const [shipToIntra] = useShipToIntraWorkflow();

  const getContextMenuItems = useCallback(
    (
      params: GetContextMenuItemsParams<Row>,
    ): Array<string | MenuItemDef<Row>> => {
      const api = params.api;
      const selected = api.getSelectedRows?.() || [];
      const focused = params.node?.data ?? undefined;

      const rows: Row[] =
        selected.length > 0 ? selected : focused?.id ? [focused] : [];
      const ids = rows.map((r) => r.id);
      const count = ids.length;
      const has = count > 0;
      const selectedCount = selected.length;
      const hasSelected = selectedCount > 0;

      const withCount = (label: string) => `${label} (${count})`;
      const withSelectedCount = (label: string) =>
        `${label} (${selectedCount})`;

      const unselectAll = () => {
        api.deselectAll?.();
        api.clearRangeSelection?.();
        api.clearFocusedCell?.();
      };

      const doInProgressAction = withToast(
        withCount('Marking as "In Progress"'),
        async () => {
          for (const id of ids) {
            await setInProgress(token, id, null);
          }
          api.refreshServerSide?.({ purge: false });
          unselectAll();
        },
      );

      const doInProgress = pastDueRestrictionEnabled
        ? async () => {
            const restriction = await checkPastDueOwners(
              rows.map((r) => r.owner),
            );
            if (restriction.blocked) {
              showPastDueRestriction(
                restriction,
                'shipping.pastDueRestrictionInProgress',
              );
              return;
            }
            await doInProgressAction();
          }
        : doInProgressAction;

      const executeWorkflow = (wfId: string, wfLabel: string) => {
        return withToast(withCount(`Running "${wfLabel}"`), async () => {
          for (const id of ids) {
            await runWorkflow(environmentId, wfId, [{ RecordId: id }]);
          }
          unselectAll();
        })();
      };

      const executeNativeWorkflow = async (runNative: typeof ship) => {
        try {
          await runNative(
            { ids },
            {
              onFinished() {
                if (!api.isDestroyed?.()) {
                  api.refreshServerSide?.({ purge: false });
                  unselectAll();
                }
              },
            },
          );
        } catch (error) {
          // Tracking owns the global notification; do not create a second page toast.
          console.error('Shipping workflow error:', error);
        }
      };

      const runWf = async (
        wfLabel: string,
        execute: () => Promise<unknown>,
      ) => {
        if (pastDueRestrictionEnabled) {
          const restriction = await checkPastDueOwners(
            rows.map((r) => r.owner),
          );
          if (restriction.blocked) {
            showPastDueRestriction(
              restriction,
              'shipping.pastDueRestrictionWorkflow',
            );
            return;
          }
        }

        if (confirmWorkflows) {
          confirmWorkflow({
            title: 'Confirm Workflow',
            description: (
              <>
                Run <b>{wfLabel}</b> workflow for {count} shipment
                {count === 1 ? '' : 's'}?
              </>
            ),
            confirmLabel: 'OK',
            cancelLabel: 'Cancel',
            onConfirm: execute,
          });
        } else {
          return execute();
        }
      };

      const editForms = () => {
        if (!has || !ids) return;
        if (onEditSelected) {
          onEditSelected(ids);
        }
        unselectAll();
      };

      const hasShippedStatus = rows.some(
        (r) => r.shipmentStatus === ShipmentStatus.SHIPPED,
      );

      const actions: MenuItemDef = {
        name: t('common.actions'),
        disabled: !has,
        subMenu: [
          {
            name: withCount('Mark as "In Progress"'),
            disabled: !has,
            action: doInProgress,
          },
        ],
      };

      const unselectItem: MenuItemDef = {
        name: withSelectedCount('Unselect All'),
        disabled: !hasSelected,
        action: unselectAll,
      };

      const workflows: MenuItemDef = {
        name: t('common.workflows'),
        disabled: !has,
        subMenu: [
          {
            name: withCount(t('workflow.names.Ship')),
            disabled: !has,
            action: () =>
              runWf(
                t('workflow.names.Ship'),
                nativeShippingEnabled
                  ? () => executeNativeWorkflow(ship)
                  : () =>
                      executeWorkflow(
                        WORKFLOW.Ship.id,
                        t('workflow.names.Ship'),
                      ),
              ),
          },
          ...(canShipToIntra
            ? [
                {
                  name: withCount(t('workflow.names.ShipToIntra')),
                  disabled: !has,
                  action: () =>
                    runWf(
                      t('workflow.names.ShipToIntra'),
                      nativeShippingEnabled
                        ? () => executeNativeWorkflow(shipToIntra)
                        : () =>
                            executeWorkflow(
                              WORKFLOW.ShipToIntra.id,
                              t('workflow.names.ShipToIntra'),
                            ),
                    ),
                },
              ]
            : []),
        ],
      };

      const printItem: MenuItemDef = {
        name: withCount('Print Shipping Form'),
        disabled: !has,
        action: () => {
          if (!has) return;
          return printForms(rows, api);
        },
      };

      const editItems: MenuItemDef = {
        name: withCount(t('common.editSelected')),
        disabled: !has || hasShippedStatus,
        action: editForms,
        tooltip: hasShippedStatus
          ? t('shipping.cannotEditShippedStatus')
          : undefined,
      };

      const viewHistory: MenuItemDef = {
        ...auditHistoryMenuItem({ rowId: focused?.id || '' }),
        disabled: !focused?.id,
      };

      return [
        viewHistory,
        'separator',
        actions,
        workflows,
        printItem,
        ...(enableShippingFormExport
          ? [
              {
                name: withCount(t('shipping.exportForm.title')),
                disabled: !has,
                subMenu: [
                  {
                    name: t('shipping.exportForm.pdf'),
                    disabled: !has,
                    action: () => {
                      if (has) {
                        setPendingExport({ shipmentIds: ids, format: 'pdf' });
                      }
                    },
                  },
                  {
                    name: t('shipping.exportForm.excel'),
                    disabled: !has,
                    action: () => {
                      if (has) {
                        setPendingExport({ shipmentIds: ids, format: 'excel' });
                      }
                    },
                  },
                ],
              },
            ]
          : []),
        editItems,
        unselectItem,
        'separator',
        'copy',
        'copyWithHeaders',
        'paste',
      ];
    },
    [
      environmentId,
      token,
      enableShippingFormExport,
      withToast,
      printForms,
      t,
      onEditSelected,
      confirmWorkflow,
      confirmWorkflows,
      nativeShippingEnabled,
      canShipToIntra,
      pastDueRestrictionEnabled,
      checkPastDueOwners,
      showPastDueRestriction,
      ship,
      shipToIntra,
    ],
  );

  return {
    pendingExport,
    closeExportDialog: () => setPendingExport(null),
    getContextMenuItems,
  };
}
